import { useEffect, useState } from 'react';
import { useCurrentUser } from '../stores/UserStore';
import { AccountPages, CONSENTS, GlobalModals } from '../constants/AppConstants';
import ModalActions from '../actions/ModalActions';
import ConsentsActions from '../actions/ConsentsActions';
import { useStoreState } from '../stores/useStoreState';
import ConsentsStore from '../stores/ConsentsStore';
import { defaultConsents } from '../records/customer/ConsentsForm';
import urls from '../urls';

export const useConsents = () => {
    const user = useCurrentUser();

    const consents = useStoreState(ConsentsStore);
    const form = consents.form;
    const isAccountPreferencePage = urls.isCurrentUrlPath(AccountPages.ACCOUNT_DETAILS);

    const [phoneVisited, setPhoneVisited] = useState(false);

    const fetchConsents = async () => {
        if (isAccountPreferencePage) {
            const consentsResponse = await ConsentsActions.getConsents();
            // getConsents resolves null on failure (and shows its own error modal)
            if (consentsResponse) {
                const updatedConsentsForm = form.normalizeConsentsResponse({ consents: consentsResponse, user });
                ConsentsActions.setConsentsForm(updatedConsentsForm);
            }
        } else {
            await ConsentsActions.getConsentsTemplate();
        }
    };

    useEffect(() => {
        fetchConsents();

        /*
            * Reset the consents form when the component unmounts
            * This wipes any invalid / unsaved changes to avoid user confusion
        */
        return () => {
            const resetForm = form.merge(defaultConsents);
            ConsentsActions.setConsentsForm(resetForm);
        };
        // eslint-disable-next-line
    }, []);

    const onCheckNotificationMethod = ({ optionId }) => () => {
        const updatedConsentsFormObj = {
            [optionId]: !consents.form.get(optionId),
        };
        if (optionId === CONSENTS.SMS_Marketing && consents.form.get(optionId)) {
            updatedConsentsFormObj[CONSENTS.PHONE] = '';
        }
        ConsentsActions.setConsentsForm(updatedConsentsFormObj);
    };

    const onChangePhoneNumber = (field, value) => {
        setPhoneVisited(true);
        const schema = form.getSchema()[field];
        if (schema) {
            const result = schema.validate(value);

            if (result.error) {
                const patchedForm = form.set(field, value);
                ConsentsActions.setConsentsForm({ [field]: value });

                // Trigger validation display
                patchedForm.validateField(field);
                return;
            }
        }

        ConsentsActions.setConsentsForm({ [field]: value });
    };

    const onChangeIsOver16 = choice => () => {
        ConsentsActions.setConsentsForm({ [CONSENTS.OVER_16]: choice });
    };

    const [submitLoading, setSubmitLoading] = useState(false);
    const handleSubmit = async () => {
        if (form.isComplete()) {
            setSubmitLoading(true);
            await ConsentsActions.updateConsentsAndUser({ form, user });
            setSubmitLoading(false);

            ModalActions.open(GlobalModals.INFO, {
                type: 'success',
                title: 'preference-centre.update-success.notification',
            });

            fetchConsents();
        }
    };

    return {
        consents: isAccountPreferencePage ? consents.response : consents.template,
        consentsForm: form,
        onCheckNotificationMethod,
        onChangePhoneNumber,
        onChangeIsOver16,
        handleSubmit,
        submitLoading,
        consentsLoading: consents.isLoading,
        showSubmitButton: isAccountPreferencePage,
        phoneVisited,
        setPhoneVisited,
    };
};
